import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCalendar, FiPlus, FiTrash2, FiZap, FiClock, FiMapPin
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoJovensService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoJovens.css';

const SEMANAS_LABEL = {
  1: '1ª semana', 2: '2ª semana', 3: '3ª semana', 4: '4ª semana', 5: '5ª semana'
};

const ReuniaoJovensAgenda = () => {
  const navigate = useNavigate();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_jovens');

  const [congregacoes, setCongregacoes] = useState([]);
  const [meses, setMeses] = useState(12);
  const [apartirDe, setApartirDe] = useState('');
  const [regras, setRegras] = useState([
    { semanas: [1, 3], congregacaoNome: 'Central',    horario: '10:00' },
    { semanas: [2, 4], congregacaoNome: 'Lourenção', horario: '10:00' },
  ]);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    congregacaoService.getAtivas().then((r) => {
      const nomes = (r?.data || []).map((c) => c.nome || c.nomeCompleto).filter(Boolean);
      setCongregacoes(nomes);
    }).catch(() => {});
  }, []);

  const toggleSemana = (idx, semana) => {
    setRegras((prev) => {
      const novo = [...prev];
      const r = { ...novo[idx] };
      const semanas = [...r.semanas];
      const i = semanas.indexOf(semana);
      if (i >= 0) semanas.splice(i, 1);
      else semanas.push(semana);
      semanas.sort();
      r.semanas = semanas;
      novo[idx] = r;
      return novo;
    });
  };

  const atualizarRegra = (idx, campo, valor) => {
    setRegras((prev) => {
      const novo = [...prev];
      novo[idx] = { ...novo[idx], [campo]: valor };
      return novo;
    });
  };

  const removerRegra = (idx) => {
    setRegras((prev) => prev.filter((_, i) => i !== idx));
  };

  const adicionarRegra = () => {
    setRegras((prev) => [
      ...prev,
      { semanas: [], congregacaoNome: '', horario: '10:00' },
    ]);
  };

  const gerar = async () => {
    const valido = regras.every((r) => r.semanas.length > 0 && r.congregacaoNome && r.horario);
    if (!valido) {
      toast.warn('Preencha todas as regras (semanas, congregação e horário)');
      return;
    }
    if (!window.confirm(`Gerar agenda para os próximos ${meses} meses?`)) return;
    try {
      setGerando(true);
      const resp = await reuniaoJovensService.gerarRecorrentes({
        meses,
        regras,
        apartirDe: apartirDe || undefined,
      });
      toast.success(
        `Agenda gerada: ${resp.criadas} reunião(ões) criada(s)` +
        (resp.ignoradas > 0 ? `, ${resp.ignoradas} já existiam` : '')
      );
      setTimeout(() => navigate('/reuniao-jovens'), 1200);
    } catch {
      toast.error('Erro ao gerar agenda');
    } finally {
      setGerando(false);
    }
  };

  return (
    <div className="rj-page">
      <button className="rj-back" onClick={() => navigate('/reuniao-jovens')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rj-page-header">
        <div>
          <h1><FiCalendar className="rj-icon-title" /> Agenda recorrente</h1>
          <p>Configure quais congregações recebem a reunião em cada domingo do mês</p>
        </div>
      </header>

      <div className="rj-aviso-info">
        <strong>Como funciona:</strong> a reunião acontece todo domingo. Defina qual congregação
        recebe em cada semana do mês (1ª, 2ª, 3ª, 4ª, 5ª) e o horário. O sistema gera as próximas
        reuniões automaticamente.
      </div>

      <section className="rj-section">
        <h2>Regras da agenda</h2>

        <div className="rj-regras-lista">
          {regras.map((r, idx) => (
            <div key={idx} className="rj-regra">
              <div className="rj-regra-header">
                <strong>Regra {idx + 1}</strong>
                {regras.length > 1 && (
                  <button
                    className="rj-btn-icone rj-btn-danger"
                    onClick={() => removerRegra(idx)}
                    disabled={!podeEditar}
                    title="Remover"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>

              <div className="rj-regra-corpo">
                <div className="rj-regra-campo">
                  <label>Semanas do mês</label>
                  <div className="rj-semanas">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`rj-chip ${r.semanas.includes(s) ? 'rj-chip-on' : ''}`}
                        onClick={() => toggleSemana(idx, s)}
                        disabled={!podeEditar}
                      >
                        {SEMANAS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rj-regra-row">
                  <label className="rj-regra-campo">
                    <span><FiMapPin /> Congregação</span>
                    <select
                      value={r.congregacaoNome}
                      onChange={(e) => atualizarRegra(idx, 'congregacaoNome', e.target.value)}
                      disabled={!podeEditar}
                    >
                      <option value="">Selecione…</option>
                      {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>

                  <label className="rj-regra-campo">
                    <span><FiClock /> Horário</span>
                    <input
                      type="time"
                      value={r.horario}
                      onChange={(e) => atualizarRegra(idx, 'horario', e.target.value)}
                      disabled={!podeEditar}
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}

          {podeEditar && (
            <button className="rj-btn rj-btn-ghost rj-btn-add-regra" onClick={adicionarRegra}>
              <FiPlus /> Adicionar regra
            </button>
          )}
        </div>
      </section>

      <section className="rj-section">
        <h2>Período</h2>
        <p className="rj-section-sub">
          Para gerar reuniões retroativas (ex.: abril/2026), defina a data inicial no passado.
          Reuniões que já existem serão ignoradas automaticamente.
        </p>
        <div className="rj-form-grid">
          <label>
            <span><FiCalendar /> A partir de (opcional)</span>
            <input
              type="date"
              value={apartirDe}
              onChange={(e) => setApartirDe(e.target.value)}
              placeholder="Padrão: hoje"
            />
          </label>
          <label>
            <span>Duração</span>
            <select value={meses} onChange={(e) => setMeses(parseInt(e.target.value, 10))}>
              <option value={1}>1 mês</option>
              <option value={2}>2 meses</option>
              <option value={3}>3 meses</option>
              <option value={6}>6 meses</option>
              <option value={12}>12 meses</option>
              <option value={24}>24 meses</option>
            </select>
          </label>
        </div>
        <div className="rj-aviso-info" style={{ marginTop: 8 }}>
          <strong>Exemplo:</strong> para gerar só abril de 2026, coloque <em>01/04/2026</em> como data inicial e <em>1 mês</em> de duração.
        </div>
      </section>

      {podeEditar && (
        <div className="rj-action-bar">
          <button
            className="rj-btn rj-btn-primary"
            onClick={gerar}
            disabled={gerando}
          >
            <FiZap /> {gerando ? 'Gerando…' : 'Gerar agenda'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReuniaoJovensAgenda;
